import React from 'react';
import { Users, CheckCircle, Clock, Package, ShoppingCart, FileText, UserPlus, Building2, Award } from 'lucide-react';

const WorkflowGuide = () => {
  const workflows = [
    {
      title: "Club Onboarding",
      icon: Users,
      color: "#dc2626",
      steps: [
        { 
          label: "Club Application", 
          description: "Club submits application via /apply/club form with organization details, contact info, and bank details",
          actor: "Club Representative"
        },
        { 
          label: "Admin Review", 
          description: "Admin reviews application in Admin Dashboard → Clubs tab → Pending Applications",
          actor: "Admin"
        },
        { 
          label: "Approval/Rejection", 
          description: "Admin approves or rejects the club. Approved clubs receive login credentials",
          actor: "Admin"
        },
        { 
          label: "Club Activated", 
          description: "Club supervisor can now log in and manage volunteer hours",
          actor: "Supervisor"
        }
      ]
    },
    {
      title: "Company/Customer Onboarding",
      icon: Building2,
      color: "#2563eb",
      steps: [
        { 
          label: "Company Application", 
          description: "Company submits application via /apply/company form with business details and contact info",
          actor: "Company Representative"
        },
        { 
          label: "Admin Review", 
          description: "Admin reviews application in Admin Dashboard → Customers tab",
          actor: "Admin"
        },
        { 
          label: "Account Creation", 
          description: "Admin creates customer account with status 'approved'",
          actor: "Admin"
        },
        { 
          label: "Company Activated", 
          description: "Company receives login credentials and can browse marketplace",
          actor: "Customer"
        }
      ]
    },
    {
      title: "Volunteer Hour Logging & Approval",
      icon: Clock,
      color: "#16a34a",
      steps: [
        { 
          label: "Volunteer Signup", 
          description: "Volunteer signs up using club-specific signup link (generated in Supervisor Dashboard)",
          actor: "Volunteer"
        },
        { 
          label: "Supervisor Approves Volunteer", 
          description: "Supervisor approves volunteer account in Supervisor Dashboard → Volunteer Management",
          actor: "Supervisor"
        },
        { 
          label: "Log Hours", 
          description: "Volunteer logs hours via Volunteer Dashboard with date, time, activity type, and description",
          actor: "Volunteer"
        },
        { 
          label: "Supervisor Review", 
          description: "Supervisor reviews pending hours in Supervisor Dashboard → Hours Approval tab",
          actor: "Supervisor"
        },
        { 
          label: "Supervisor Approval", 
          description: "Supervisor approves or rejects hours. Approved hours move to 'Platform Approval' status",
          actor: "Supervisor"
        },
        { 
          label: "Admin Final Approval", 
          description: "Admin gives final approval in Admin Dashboard → Approve Hours tab",
          actor: "Admin"
        },
        { 
          label: "Hours Available for Bundling", 
          description: "Approved hours are now available to be included in bundles",
          actor: "System"
        }
      ]
    },
    {
      title: "Bundle Creation",
      icon: Package,
      color: "#ea580c",
      steps: [
        { 
          label: "View Unbundled Hours", 
          description: "Admin views approved hours in Admin Dashboard → Create Bundles tab",
          actor: "Admin"
        },
        { 
          label: "Filter Hours", 
          description: "Admin filters hours by country, county, club, activity type, or volunteer",
          actor: "Admin"
        },
        { 
          label: "Preview & Create", 
          description: "Admin previews filtered hours, sets bundle name and price per hour, then creates bundle",
          actor: "Admin"
        },
        { 
          label: "Bundle Listed", 
          description: "Bundle appears in Admin Dashboard → Hours Bundles tab and Customer Marketplace",
          actor: "System"
        }
      ]
    },
    {
      title: "Purchase & Token Minting",
      icon: ShoppingCart,
      color: "#7c3aed",
      steps: [
        { 
          label: "Browse Marketplace", 
          description: "Customer views available bundles in Customer Dashboard → Marketplace tab",
          actor: "Customer"
        },
        { 
          label: "Purchase Bundle", 
          description: "Customer clicks 'Purchase' and completes payment via Stripe",
          actor: "Customer"
        },
        { 
          label: "Payment Processing", 
          description: "System processes payment: 90% to clubs (split by hours contributed), 10% platform fee",
          actor: "System"
        },
        { 
          label: "Blockchain Tokens Minted", 
          description: "Soulbound tokens are minted on Polygon blockchain for each volunteer hour in the bundle",
          actor: "System"
        },
        { 
          label: "ESG Report Generated", 
          description: "Comprehensive ESG report is automatically generated with impact metrics and blockchain verification",
          actor: "System"
        },
        { 
          label: "Access Report", 
          description: "Customer downloads ESG report from Customer Dashboard → ESG Reports tab",
          actor: "Customer"
        }
      ]
    },
    {
      title: "Payment Distribution",
      icon: Award,
      color: "#059669",
      steps: [
        { 
          label: "Payment Split Calculated", 
          description: "System calculates each club's share based on volunteer hours contributed to purchased bundle",
          actor: "System"
        },
        { 
          label: "Club Earnings Updated", 
          description: "Each club's earnings are updated in database (90% of total, split proportionally)",
          actor: "System"
        },
        { 
          label: "View Earnings", 
          description: "Supervisors view earnings in Supervisor Dashboard → Earnings tab",
          actor: "Supervisor"
        },
        { 
          label: "Admin Processes Payouts", 
          description: "Admin views pending payouts in Admin Dashboard → Payments tab → By Club view",
          actor: "Admin"
        },
        { 
          label: "Funds Transferred", 
          description: "Admin transfers funds to club bank accounts and marks payments as 'paid'",
          actor: "Admin"
        }
      ]
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '40px 20px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{ 
            fontSize: '2.5rem', 
            fontWeight: '700', 
            color: '#111827', 
            marginBottom: '12px',
            background: 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            PlayBack Token - Platform Workflow
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#6b7280', maxWidth: '800px', margin: '0 auto' }}>
            Complete step-by-step guide showing how clubs and companies onboard, how volunteer hours are logged and approved, and how purchases work
          </p>
        </div>

        {/* Overview Card */}
        <div style={{
          background: 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '48px',
          color: 'white',
          boxShadow: '0 10px 30px rgba(220, 38, 38, 0.3)'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '600', marginBottom: '16px', color: 'white' }}>
            System Overview
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '4px' }}>3</div>
              <div style={{ fontSize: '0.95rem', opacity: 0.9 }}>User Roles</div>
              <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>Admin, Supervisor, Volunteer, Customer</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '4px' }}>6</div>
              <div style={{ fontSize: '0.95rem', opacity: 0.9 }}>Core Workflows</div>
              <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>From onboarding to payment</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '4px' }}>90/10</div>
              <div style={{ fontSize: '0.95rem', opacity: 0.9 }}>Revenue Split</div>
              <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>Clubs get 90%, Platform gets 10%</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '4px' }}>Blockchain</div>
              <div style={{ fontSize: '0.95rem', opacity: 0.9 }}>Polygon Network</div>
              <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>Soulbound tokens for verification</div>
            </div>
          </div>
        </div>

        {/* Workflows */}
        <div style={{ display: 'grid', gap: '32px' }}>
          {workflows.map((workflow, workflowIndex) => {
            const IconComponent = workflow.icon;
            return (
              <div 
                key={workflowIndex}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '32px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                  border: `3px solid ${workflow.color}`,
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.07)';
                }}
              >
                {/* Workflow Title */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    background: workflow.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <IconComponent size={32} color="white" />
                  </div>
                  <div>
                    <h3 style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: '700', 
                      color: '#111827',
                      marginBottom: '4px'
                    }}>
                      {workflow.title}
                    </h3>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: workflow.color,
                      fontWeight: '600'
                    }}>
                      {workflow.steps.length} Steps
                    </div>
                  </div>
                </div>

                {/* Steps */}
                <div style={{ position: 'relative', paddingLeft: '40px' }}>
                  {/* Vertical line */}
                  <div style={{
                    position: 'absolute',
                    left: '15px',
                    top: '8px',
                    bottom: '8px',
                    width: '3px',
                    background: `linear-gradient(to bottom, ${workflow.color} 0%, ${workflow.color}40 100%)`
                  }} />

                  {workflow.steps.map((step, stepIndex) => (
                    <div 
                      key={stepIndex}
                      style={{ 
                        position: 'relative',
                        marginBottom: stepIndex === workflow.steps.length - 1 ? 0 : '24px'
                      }}
                    >
                      {/* Step number circle */}
                      <div style={{
                        position: 'absolute',
                        left: '-40px',
                        top: '4px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: workflow.color,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.875rem',
                        fontWeight: '700',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1
                      }}>
                        {stepIndex + 1}
                      </div>

                      {/* Step content */}
                      <div style={{
                        background: '#f9fafb',
                        padding: '16px 20px',
                        borderRadius: '10px',
                        border: '2px solid #e5e7eb',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.borderColor = workflow.color;
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f9fafb';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <h4 style={{ 
                            fontSize: '1.05rem', 
                            fontWeight: '600', 
                            color: '#111827',
                            margin: 0
                          }}>
                            {step.label}
                          </h4>
                          <span style={{
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            color: workflow.color,
                            background: `${workflow.color}15`,
                            padding: '4px 12px',
                            borderRadius: '12px',
                            whiteSpace: 'nowrap',
                            marginLeft: '12px'
                          }}>
                            {step.actor}
                          </span>
                        </div>
                        <p style={{ 
                          fontSize: '0.95rem', 
                          color: '#4b5563',
                          lineHeight: '1.6',
                          margin: 0
                        }}>
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Key Points */}
        <div style={{
          marginTop: '48px',
          background: 'white',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
          border: '3px solid #f59e0b'
        }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '24px' }}>
            🔑 Key Points to Remember
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div style={{ padding: '16px', background: '#fef3c7', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
              <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '6px' }}>Multi-Level Approval</div>
              <div style={{ fontSize: '0.9rem', color: '#78350f' }}>
                Volunteer hours require supervisor approval + admin approval before bundling
              </div>
            </div>
            <div style={{ padding: '16px', background: '#dbeafe', borderRadius: '8px', borderLeft: '4px solid #2563eb' }}>
              <div style={{ fontWeight: '600', color: '#1e3a8a', marginBottom: '6px' }}>Revenue Distribution</div>
              <div style={{ fontSize: '0.9rem', color: '#1e40af' }}>
                90% goes to clubs (proportionally), 10% platform fee - automatically calculated
              </div>
            </div>
            <div style={{ padding: '16px', background: '#dcfce7', borderRadius: '8px', borderLeft: '4px solid #16a34a' }}>
              <div style={{ fontWeight: '600', color: '#14532d', marginBottom: '6px' }}>Blockchain Verification</div>
              <div style={{ fontSize: '0.9rem', color: '#15803d' }}>
                Each hour becomes a soulbound token on Polygon - permanent, non-transferable proof
              </div>
            </div>
            <div style={{ padding: '16px', background: '#fce7f3', borderRadius: '8px', borderLeft: '4px solid #db2777' }}>
              <div style={{ fontWeight: '600', color: '#831843', marginBottom: '6px' }}>Admin Controls All</div>
              <div style={{ fontSize: '0.9rem', color: '#9f1239' }}>
                Admin approves clubs, customers, hours, creates bundles, and processes payouts
              </div>
            </div>
          </div>
        </div>

        {/* Application Links */}
        <div style={{
          marginTop: '32px',
          padding: '24px',
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderRadius: '12px',
          border: '2px solid #fbbf24'
        }}>
          <h4 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#78350f', marginBottom: '16px' }}>
            📝 Application Forms
          </h4>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <a 
              href="/apply/club" 
              style={{
                padding: '12px 24px',
                background: '#dc2626',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'background 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
            >
              <Users size={18} />
              Club Application Form
            </a>
            <a 
              href="/apply/company" 
              style={{
                padding: '12px 24px',
                background: '#2563eb',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'background 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
            >
              <Building2 size={18} />
              Company Application Form
            </a>
            <a 
              href="/test-credentials" 
              style={{
                padding: '12px 24px',
                background: '#16a34a',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'background 0.2s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#15803d'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#16a34a'}
            >
              <UserPlus size={18} />
              Test Credentials
            </a>
            <a 
              href="/" 
              style={{
                padding: '12px 24px',
                background: 'white',
                color: '#111827',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'all 0.2s',
                border: '2px solid #d1d5db'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.borderColor = '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
            >
              ← Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowGuide;
